/**
 * The quick filter — `docs/list-filters.md` §4.1, drawn from
 * `design/mockups/30-list-filters/html/filters-01-quick.html`.
 *
 * What it replaces is the three-step filter, which asked three questions in order —
 * which field, which condition, which value — and applied the answer at the
 * end. Three steps to say "and scheduled too", and no way to see what the
 * list already had on it without stepping through. It was a form for building
 * one condition, on a screen whose whole point is that you are *looking* at
 * something and adjusting it.
 *
 * This is the same information as a panel of sections. The area's quick
 * fields are open, everything else is one search away, and **it applies as
 * you go**: ticking Quoted narrows the table underneath before you let go of
 * the mouse. Done just closes — there is nothing to apply, because it already
 * is. That is the difference between a filter you build and a filter you
 * adjust.
 *
 * One condition per field, at the top level. A filter that carries a group or
 * a related condition — the advanced builder's work — is shown as a count in
 * the footer and left untouched, because a popover that quietly dropped the
 * half it could not draw would be worse than one that admits it.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  type FilterCondition, type FilterGroup, type FilterNode,
} from '@rowted/shared'
import { Popover } from './overlays.js'
import { PanelSheet } from './panel-sheet.js'
import { Button } from './primitives.js'
import { KindControl, summaryOf } from './filter-kind-controls.js'
import { COLUMN_ICONS, IconCheck, IconChevronDown, IconChevronRight, IconClose, IconPlus, IconSearch } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import { usePhone } from '../lib/use-narrow.js'
import { conditionFor, withFieldCondition } from '../lib/use-list.js'
import type { ListFieldDef, UseListResult } from '../lib/use-list.js'

/** Every control menu is this wide (`filters-01-quick.html`). */
export const QUICK_WIDTH = 440

export interface QuickFilterProps {
  list: UseListResult
  open: boolean
  onClose: () => void
  /** The button it hangs from, so `placeFloating` can keep it on screen. */
  anchor?: HTMLElement | null
  /** A column a header's "Filter by…" sent here; it opens on that section. */
  focusField?: string | null
  /**
   * When given, the popover edits *this* group instead of the list's own
   * filter — the card builder's "Which records". The list is still what the
   * catalogue and the counts come from, because a card's records are rows of
   * this list.
   */
  value?: FilterGroup
  onChange?: (next: FilterGroup) => void
  /**
   * Opens the advanced builder on whatever is in here now.
   *
   * A prop rather than something this component owns: what it opens is a
   * `Modal`, and a modal owned by a popover is a modal that closes when the
   * popover does.
   */
  onAdvanced?: () => void
}

export function QuickFilter({
  list, open, onClose, anchor, focusField, value, onChange, onAdvanced,
}: QuickFilterProps) {
  const phone = usePhone()
  const detached = Boolean(onChange)
  const filter = detached ? (value ?? { match: 'and', conditions: [] }) : list.view.filter

  /*
   * The field whose own sheet is up, on a phone (§4.5). A relation is a
   * search over hundreds of rows; expanded inside a 48px row it is a list
   * inside a list, and the keyboard takes what is left of the screen. So it
   * gets the screen: its own sheet over this one, with a way back.
   */
  const [pushed, setPushed] = useState<string | null>(null)

  const [query, setQuery] = useState('')
  const [opened, setOpened] = useState<string[]>([])
  const [browsing, setBrowsing] = useState(false)
  const search = useRef<HTMLInputElement>(null)

  const filterable = useMemo(
    () => list.fields.filter((field) => field.filterable),
    [list.fields],
  )

  /** Fields that already carry a condition, in the order the filter holds them. */
  const answered = useMemo(() => (filter?.conditions ?? [])
    .flatMap((node: FilterNode) => ('field' in node ? [node.field] : []))
    .filter((name) => filterable.some((field) => field.name === name)),
  [filter, filterable])

  /*
   * Sections, in order: the area's quick fields, then anything somebody has
   * answered that is not already one of them, then whatever has been opened
   * from More fields. A field never appears twice, and the order does not
   * shuffle while you are using it.
   */
  const sections = useMemo(() => {
    const names: string[] = []
    for (const field of filterable) if (field.quick) names.push(field.name)
    for (const name of answered) if (!names.includes(name)) names.push(name)
    for (const name of opened) if (!names.includes(name)) names.push(name)
    return names
      .map((name) => filterable.find((field) => field.name === name))
      .filter((field): field is ListFieldDef => Boolean(field))
  }, [filterable, answered, opened])

  /** Which sections are expanded. A quick field opens closed; an answered one open. */
  const [expanded, setExpanded] = useState<string[]>([])
  useEffect(() => {
    if (!open) return
    setExpanded((current) => {
      const wanted = focusField ? [focusField] : []
      const already = current.filter((name) => sections.some((field) => field.name === name))
      const next = [...new Set([...already, ...wanted])]
      return next.length === already.length && wanted.length === 0 ? current : next
    })
    if (focusField) setOpened((current) =>
      (current.includes(focusField) ? current : [...current, focusField]))
  }, [open, focusField])

  // Closing forgets what was open, so it does not reopen three sections deep
  // next time from a state nobody remembers setting.
  useEffect(() => {
    if (open) return
    setQuery('')
    setBrowsing(false)
    setOpened([])
    setExpanded([])
    setPushed(null)
  }, [open])

  const write = (next: FilterGroup) => {
    if (onChange) onChange(next)
    else list.setFilter(next.conditions)
  }

  const setField = (field: string, condition: FilterCondition | null) =>
    write(withFieldCondition(filter, field, condition))

  /**
   * The rest of the filter, for a control's counts.
   *
   * This field's own condition is left out: ticking Quoted must not send
   * Accepted's count to zero, because the two are alternatives within one
   * question, not two questions.
   */
  const restOf = (field: string): FilterGroup =>
    withFieldCondition(filter, field, null)

  /*
   * A detached filter has no list behind it to count, so it asks. The list's
   * own filter needs no second query at all — the table underneath already
   * went and got the answer, and `visibleCount` is it.
   */
  const detachedCount = useDetachedCount(list, detached ? filter : undefined)
  const matching = detached ? detachedCount : list.visibleCount

  const matches = filterable.filter((field) =>
    field.label.toLowerCase().includes(query.trim().toLowerCase()))

  const openSection = (name: string) => {
    setOpened((current) => (current.includes(name) ? current : [...current, name]))
    // On a phone a relation takes the screen rather than the row under it.
    if (phone && list.fields.find((field) => field.name === name)?.kind === 'relation') {
      setPushed(name)
    } else {
      setExpanded((current) => (current.includes(name) ? current : [...current, name]))
    }
    setBrowsing(false)
    setQuery('')
  }

  const clearAll = () => write({ match: filter?.match ?? 'and', conditions: [] })
  const answeredCount = filter?.conditions.length ?? 0

  /*
   * The count in words. `list.area` is the noun the area is called, which is
   * what "Show 118 jobs" needs \u2014 a hard-coded "records" would be right on no
   * screen in the product.
   */
  const showLabel = matching === null
    ? 'Show what matches'
    : `Show ${formatNumber(matching)} ${list.area}`

  /*
   * The search well, the sections and More fields — the same markup on both
   * shells, which is §4.5's rule: a phone does not get a smaller filter, it
   * gets the same filter in a box that reaches the bottom of the screen
   * instead of hanging off a 32px button.
   */
  const panel = (
    <>
      <div className="shrink-0 px-3.5 pb-2 pt-2.5">
        <label className={cn('flex items-center gap-2 rounded-control border',
          phone ? 'h-10' : 'h-[34px]',
          'border-line bg-surface px-2.5 focus-within:ring-2 focus-within:ring-accent-ring')}
        >
          <span aria-hidden className="shrink-0 text-text-muted"><IconSearch size={14} /></span>
          <input
            ref={search}
            value={query}
            onChange={(event) => { setQuery(event.target.value); setBrowsing(true) }}
            aria-label={`Filter by any of ${filterable.length} fields`}
            placeholder={`Filter by any of ${filterable.length} fields…`}
            className="w-full min-w-0 bg-transparent text-cell text-text placeholder:text-text-faint focus:outline-none focus-visible:shadow-none"
          />
        </label>
      </div>

      <div className={cn('min-h-0 flex-1 border-t border-line-faint',
        // On a phone the sheet is the scroller; here the column is.
        phone ? '' : 'overflow-y-auto')}
      >
        {browsing
          ? (
            <FieldCatalogue
              fields={matches}
              onPick={openSection}
              total={filterable.length}
            />
          )
          : (
            <>
              {sections.map((field) => (
                <Section
                  key={field.name}
                  list={list}
                  field={field}
                  phone={phone}
                  condition={conditionFor(filter, field.name)}
                  expanded={expanded.includes(field.name)}
                  onToggle={() => {
                    if (phone && field.kind === 'relation') { setPushed(field.name); return }
                    setExpanded((current) => (current.includes(field.name)
                      ? current.filter((name) => name !== field.name)
                      : [...current, field.name]))
                  }}
                  onChange={(next) => setField(field.name, next)}
                  narrowedBy={restOf(field.name)}
                />
              ))}

              <button
                type="button"
                onClick={() => { setBrowsing(true); search.current?.focus() }}
                className={cn('flex w-full items-center gap-1.5 px-3.5 text-left',
                  phone ? 'h-12' : 'h-10',
                  'text-cell font-semibold text-accent hover:bg-surface-alt')}
              >
                <IconPlus size={13} />
                More fields
                <span className="ml-1 font-medium text-text-faint">
                  · {formatNumber(Math.max(0, filterable.length - sections.length))} more,
                  {' '}any of them
                </span>
              </button>
            </>
          )}
      </div>
    </>
  )

  if (phone) {
    const field = pushed
      ? filterable.find((candidate) => candidate.name === pushed) ?? null
      : null
    const condition = field ? conditionFor(filter, field.name) : null

    return (
      <>
        <PanelSheet
          open={open}
          onClose={onClose}
          title="Filter"
          sub={matching === null
            ? 'Counting…'
            : `${formatNumber(matching)} of ${formatNumber(list.totalCount)} match · live`}
          action={answeredCount > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="h-9 rounded-button px-2 text-cell font-semibold text-accent"
            >
              Clear all
            </button>
          )}
          footer={
            <>
              {onAdvanced && (
                <button
                  type="button"
                  onClick={onAdvanced}
                  className="shrink-0 text-cell font-semibold text-accent"
                >
                  Advanced…
                </button>
              )}
              {/*
                * The primary says what it will do rather than "Apply": the
                * filter is already on — every tick applied as it was made —
                * so this closes the sheet and shows the list it has been
                * narrowing all along, and the number is the proof.
                */}
              <Button
                variant="primary"
                size="big"
                onClick={onClose}
                className="ml-auto flex-1 justify-center gap-1.5"
              >
                <IconCheck size={16} />
                {showLabel}
              </Button>
            </>
          }
        >
          {panel}
        </PanelSheet>

        {/*
          * A relation, on its own screen, over the sheet that sent you here.
          * Back rather than Close: it is one question inside the filter, and
          * closing the lot to answer it would lose the place.
          */}
        {field && (
          <PanelSheet
            open
            onClose={onClose}
            onBack={() => setPushed(null)}
            layer={70}
            title={field.label}
            sub={summaryOf(field, condition, list.labelCache)}
            footer={
              <Button
                variant="primary"
                size="big"
                onClick={() => setPushed(null)}
                className="flex-1 justify-center gap-1.5"
              >
                <IconCheck size={16} />
                Done
              </Button>
            }
          >
            <div className="px-3.5 py-3">
              <KindControl
                list={list}
                field={field}
                value={condition}
                onChange={(next) => setField(field.name, next)}
                narrowedBy={restOf(field.name)}
              />
            </div>
          </PanelSheet>
        )}
      </>
    )
  }

  return (
    <Popover
      open={open}
      onClose={onClose}
      label="Filter"
      width={QUICK_WIDTH}
      anchor={anchor}
      className="rounded-menu"
      maxHeight={760}
    >
      {/*
        * `min-h-0 flex-1`, and **no max-height of its own**.
        *
        * This had one — `max-h-[min(72vh,760px)]` — and the popover it sits
        * in has one too, measured against the room under the button. Two caps
        * on one column do not agree: when the popover's was the smaller, this
        * one did not apply and the sections ran past the bottom edge; and
        * without `min-h-0` a flex item refuses to shrink below its content,
        * so the scroll box never got a bounded height and the footer was
        * pushed off the end. Add enough fields and the buttons disappeared.
        *
        * One cap, the popover's, and everything inside it shrinks to fit.
        */}
      <div className="flex min-h-0 flex-1 flex-col">
        {panel}

        <footer className="flex shrink-0 items-center gap-2 border-t border-line bg-surface-alt px-3.5 py-2.5">
          <span className="flex items-center gap-1.5 text-sub text-text-muted">
            <span aria-hidden className="h-[7px] w-[7px] rounded-full bg-accent" />
            {matching === null
              ? 'Counting…'
              : `${formatNumber(matching)} of ${formatNumber(list.totalCount)} match · live`}
          </span>
          <span className="ml-auto flex items-center gap-2">
            {/*
              * **Clear all, in the footer** (Aaron).
              *
              * Every section has its own × and the toolbar has a Clear beside
              * the chips, so the one place you could not undo a filter was
              * the panel you were building it in: six sections meant six
              * clicks and a scroll to find the sixth. It sits with the other
              * two words rather than beside Done, which is the button that
              * closes rather than the one that changes anything.
              *
              * Absent, not greyed, when nothing is set — the same rule the
              * section's own × follows one line above.
              */}
            {answeredCount > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="mr-1.5 text-cell font-semibold text-accent hover:text-accent-dark"
              >
                Clear all
              </button>
            )}
            {onAdvanced && (
              <button
                type="button"
                onClick={onAdvanced}
                className="mr-1.5 text-cell font-semibold text-accent hover:text-accent-dark"
              >
                Advanced…
              </button>
            )}
            <Button variant="primary" size="toolbar" onClick={onClose} className="gap-1.5">
              <IconCheck size={15} />
              Done
            </Button>
          </span>
        </footer>
      </div>
    </Popover>
  )
}

// ---------------------------------------------------------------------------
// One section
// ---------------------------------------------------------------------------

function Section({
  list, field, phone, condition, expanded, onToggle, onChange, narrowedBy,
}: {
  list: UseListResult
  field: ListFieldDef
  /** 48px rows and no inline expansion for a relation (§4.5). */
  phone?: boolean
  condition: FilterCondition | null
  expanded: boolean
  onToggle: () => void
  onChange: (next: FilterCondition | null) => void
  narrowedBy: FilterGroup
}) {
  const Icon = COLUMN_ICONS[field.icon]
  const summary = summaryOf(field, condition, list.labelCache)
  const set = condition !== null
  /* A relation goes to its own sheet on a phone, so it never opens in place. */
  const pushes = Boolean(phone) && field.kind === 'relation'

  return (
    <div className="border-b border-line-faint">
      <div className={cn('flex items-center gap-2 px-3.5', phone ? 'h-12' : 'h-10')}>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={pushes ? undefined : expanded}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <span aria-hidden className="shrink-0 text-text-muted"><Icon size={14} /></span>
          <span className="truncate text-body font-bold text-text">{field.label}</span>
          <span className={cn('ml-auto truncate whitespace-nowrap text-sub',
            set ? 'font-semibold text-accent-dark' : 'font-medium text-text-faint')}
          >
            {summary}
          </span>
        </button>

        {/*
          * The × only when there is something to clear. A permanent one is a
          * button that does nothing on most rows, and on the rows where it
          * does something it is indistinguishable from the ones where it
          * does not.
          */}
        {set && (
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label={`Clear ${field.label}`}
            className="shrink-0 text-text-faint hover:text-text"
          >
            <IconClose size={12} />
          </button>
        )}
        <span aria-hidden className="shrink-0 text-text-faint">
          {expanded && !pushes
            ? <IconChevronDown size={13} />
            : <IconChevronRight size={13} />}
        </span>
      </div>

      {expanded && !pushes && (
        <div className="px-3.5 pb-3">
          <KindControl
            list={list}
            field={field}
            value={condition}
            onChange={onChange}
            narrowedBy={narrowedBy}
          />
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// More fields
// ---------------------------------------------------------------------------

function FieldCatalogue({
  fields, onPick, total,
}: { fields: ListFieldDef[]; onPick: (name: string) => void; total: number }) {
  if (fields.length === 0) {
    return (
      <p className="px-3.5 py-6 text-center text-cell text-text-muted">
        No column matches that.
      </p>
    )
  }

  /*
   * A flat list with the group on the right, not headings —
   * `filters-02-kinds.html` draws it that way and it is the better answer:
   * "Invoiced · Money" beside "Invoiced on · Dates" is how you tell the two
   * apart, and a heading would separate them onto different screenfuls of a
   * list somebody is scanning for a word they half remember.
   */
  return (
    <div className="p-2">
      <p className="px-2 pb-0.5 pt-1.5 text-label uppercase text-text-faint">
        {fields.length === total ? `Every field · ${total}` : `Matches · ${fields.length}`}
      </p>
      <ul className="flex list-none flex-col">
        {fields.map((field) => {
          const Icon = COLUMN_ICONS[field.icon]
          return (
            <li key={field.name}>
              <button
                type="button"
                onClick={() => onPick(field.name)}
                className="flex h-8 w-full items-center gap-2 rounded-button px-2 text-left text-cell text-text hover:bg-surface-alt"
              >
                <span aria-hidden className="shrink-0 text-text-faint">
                  <Icon size={13} />
                </span>
                <span className="min-w-0 flex-1 truncate">{field.label}</span>
                {field.group && (
                  <span className="shrink-0 text-sub text-text-faint">{field.group}</span>
                )}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Counting a filter that has no list behind it
// ---------------------------------------------------------------------------

/**
 * How many rows a detached filter would match — the card builder's case.
 *
 * Debounced, because this is a round trip per keystroke otherwise. The list's
 * own filter needs none of this: the table underneath has already asked.
 */
function useDetachedCount(
  list: UseListResult, filter: FilterGroup | undefined,
): number | null {
  const [count, setCount] = useState<number | null>(null)
  const shape = JSON.stringify(filter?.conditions ?? [])

  useEffect(() => {
    if (!filter) { setCount(null); return undefined }
    let cancelled = false
    /*
     * 200ms, and the timer is the debounce. Without it this is a round trip
     * per keystroke in a money box, and the answers come back out of order:
     * the count for "2" arriving after the count for "2500" leaves the wrong
     * number under a filter nobody typed.
     */
    const timer = setTimeout(() => {
      void list.countMatching(filter)
        .then((result) => { if (!cancelled) setCount(result) })
        .catch(() => { if (!cancelled) setCount(null) })
    }, 200)
    return () => { cancelled = true; clearTimeout(timer) }
    // `shape` rather than `filter`: a fresh object every render would make
    // this fire forever.
  }, [shape])

  return count
}
